/**
 * Recent bidding activity.
 *
 * The feed is pushed: each entry is a payment that cleared, delivered over the
 * same socket the hero board listens on. The board it sits beside is the seed —
 * the most recently raised listings, newest first — and everything after that
 * arrives rather than being polled for.
 *
 * When there is no socket the seed is all there is, and it is re-read on the
 * same slow timer the board uses. The feed then lags by up to a minute, which
 * is the honest cost of having no channel; it never fills the gap with
 * activity that did not happen.
 *
 * The newest row slides in from above; the rows below it fade toward the
 * bottom of the card, which is the artboard's treatment and reads as a feed
 * running rather than a list sitting still.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';

import { MINIMUM_FIRST_BID_CENTS, type RankedEntry, type TickerEvent } from '@bidtohire/shared';

import { shortTimeAgo, timeAgo } from '../lib/format';
import { useLiveBids } from '../lib/live';
import { mergeTickerEvents, tickerEventFromBid } from '../lib/live-board';
import { Money } from './Money';
import { CompanyMark, LiveDot } from './ui';

/**
 * Derive ticker events from board rows that have been paid for.
 *
 * These are a seed, not a feed. They come from the leaderboard page already on
 * screen, which is ordered by money and — while totals are tied — by who paid
 * first, so the rows available here are the board's OLDEST. Anything newer
 * arrives over the socket. The panel says "on the listings shown" rather than
 * claiming to be the freshest activity on the site, because it is not: naming
 * these the latest bids would be false on any board deeper than one page.
 */
export function tickerEventsFromEntries(
  entries: readonly RankedEntry[],
  limit = 6,
): TickerEvent[] {
  return entries
    .filter((entry): entry is RankedEntry & { lastBidAt: string } => entry.lastBidAt !== undefined)
    .sort((a, b) => Date.parse(b.lastBidAt) - Date.parse(a.lastBidAt))
    .slice(0, limit)
    .map((entry) => ({
      jobId: entry.jobId,
      jobTitle: entry.title,
      jobSlug: entry.slug,
      companyName: entry.company.name,
      companySlug: entry.company.slug,
      // Only the running total is public per listing; the size of an
      // individual raise arrives with the live feed.
      amountCents: entry.totalPaidCents,
      totalCents: entry.totalPaidCents,
      at: entry.lastBidAt,
    }));
}

/**
 * The feed as it should currently read: whatever the board seeded it with,
 * plus every bid pushed since, newest first and one entry per listing.
 *
 * A pushed entry outranks the seed for the same listing because it carries the
 * size of the raise, which a board row cannot state — only lifetime totals are
 * public per listing.
 */
export function useLiveTicker(seed: readonly TickerEvent[], limit = 6): readonly TickerEvent[] {
  const [pushed, setPushed] = useState<readonly TickerEvent[]>([]);
  const latestSeed = useRef(seed);
  latestSeed.current = seed;

  useLiveBids({
    onBid: (event) => {
      setPushed((current) => mergeTickerEvents(current, [tickerEventFromBid(event)], limit));
    },
    // Nothing to do on a reconnect: the seed comes from the board's own read,
    // which the board has already asked for by the time this runs.
    onResume: undefined,
  });

  // Anything the board has since caught up on can be dropped from the pushed
  // list, so a listing is not held in memory for the life of the page.
  useEffect(() => {
    setPushed((current) =>
      current.filter((event) =>
        latestSeed.current.every(
          (seeded) => seeded.jobId !== event.jobId || Date.parse(seeded.at) < Date.parse(event.at),
        ),
      ),
    );
  }, [seed]);

  return mergeTickerEvents(seed, pushed, limit);
}

/** Rows fade out toward the foot of the feed, oldest quietest. */
function rowOpacity(index: number, count: number): number | undefined {
  if (count < 4) return undefined;
  if (index === count - 1) return 0.3;
  if (index === count - 2) return 0.62;
  return undefined;
}

export function LiveTicker({ events }: { readonly events: readonly TickerEvent[] }): ReactNode {
  return (
    <section className="card tickercard" aria-label="Recent bids">
      <div className="tickerhead">
        <div className="row" style={{ gap: 8 }}>
          <LiveDot />
          <span className="label">Recent bids on the listings shown</span>
        </div>
      </div>

      {events.length === 0 ? (
        <p className="tickerempty">
          No bids yet. The first <Money cents={MINIMUM_FIRST_BID_CENTS} /> takes the top spot.
        </p>
      ) : (
        <div aria-live="polite">
          {events.map((event, index) => (
            <Link
              className="tickrow"
              key={`${event.jobId}-${event.at}`}
              to={`/jobs/${event.jobId}`}
              style={{ opacity: rowOpacity(index, events.length) }}
            >
              <CompanyMark company={{ name: event.companyName, slug: event.companySlug }} />
              <span style={{ minWidth: 0 }}>
                <span className="company" style={{ display: 'block' }}>
                  {event.companyName}
                </span>
                <span className="role" style={{ display: 'block' }}>
                  {event.jobTitle}
                </span>
              </span>
              <span>
                <span className="amount mono" style={{ display: 'block' }}>
                  <Money cents={event.totalCents} />
                </span>
                <span className="when">{timeAgo(event.at)}</span>
              </span>
            </Link>
          ))}
        </div>
      )}
    </section>
  );
}

/** The single-line strip shown on narrow screens, per the mobile artboard. */
export function TickerStrip({ events }: { readonly events: readonly TickerEvent[] }): ReactNode {
  const latest = events[0];
  if (latest === undefined) return null;

  return (
    <Link className="tickerstrip" to={`/jobs/${latest.jobId}`}>
      <LiveDot />
      <span className="company">{latest.companyName}</span>
      <span className="role">{latest.jobTitle}</span>
      <span className="amount mono">
        <Money cents={latest.totalCents} />
      </span>
      <span className="when mono">{shortTimeAgo(latest.at)}</span>
    </Link>
  );
}
